// eslint-disable-next-line prettier/prettier
import {
  AttachmentResponse as AddresseeAttachmentResponse
} from '@sdir/httpclient/lib/clients/core/commonaddressee';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  downloadAddresseeAttachment,
  getAttachmentsForAddressee,
} from '../../../helpers/addresseeApiHelpers';
import { CurrentAddresseeContext, ICurrentAddresseeContext } from '../../../services/contexts';
import { useCurrentPerson } from '../../../services/hooks';
import { Loadable } from '../../../types';

export const CurrentAddresseeProvider: FC<{ legalEntityId: string | null; children: any }> = ({
  children,
  legalEntityId,
}) => {
  const currentLegalEntityId = useRef<string | null | undefined>(legalEntityId);
  const attachmentCache = useRef<{ [blobId: string]: File }>({});

  const [attachments, setAttachments] = useState<Loadable<AddresseeAttachmentResponse[]>>({});

  const loadAttachments = useCallback(async () => {
    const idToLoadFor = currentLegalEntityId.current;
    if (!idToLoadFor) {
      setAttachments({});
      return;
    }

    setAttachments({ loading: true });
    try {
      const attachmentsResult = await getAttachmentsForAddressee(idToLoadFor);
      if (idToLoadFor === currentLegalEntityId.current) {
        setAttachments({ result: attachmentsResult.data });
      }
    } catch (e: any) {
      if (idToLoadFor === currentLegalEntityId.current) {
        setAttachments({ error: e });
      }
    }
  }, []);

  const getAttachmentFile = useCallback(
    async (attachment: AddresseeAttachmentResponse): Promise<File> => {
      if (!legalEntityId || !attachment.uid)
        throw new Error('Invalid legal entity ID or attachment UID');

      if (attachmentCache.current[attachment.uid]) return attachmentCache.current[attachment.uid];
      const legalEntityToLoadFor = currentLegalEntityId;
      const file = await downloadAddresseeAttachment(legalEntityId, attachment);
      if (legalEntityToLoadFor === currentLegalEntityId) {
        attachmentCache.current = { ...attachmentCache.current, [attachment.uid]: file };
      }
      return file;
    },
    [legalEntityId]
  );

  useEffect(() => {
    attachmentCache.current = {};
    currentLegalEntityId.current = legalEntityId;
    if (currentLegalEntityId.current) {
      loadAttachments();
    }
  }, [legalEntityId, loadAttachments]);

  const value: ICurrentAddresseeContext = {
    addresseeAttachments: attachments,
    getAddresseeAttachment: getAttachmentFile,
  };

  return (
    <CurrentAddresseeContext.Provider value={value}>{children}</CurrentAddresseeContext.Provider>
  );
};

export const AddresseeForCurrentPersonProvider: FC<{ children: any }> = ({ children }) => {
  const { person } = useCurrentPerson();
  const legalEntityIdForCurrentPerson = person?.result?.legalEntityId;
  return (
    <CurrentAddresseeProvider legalEntityId={legalEntityIdForCurrentPerson || null}>
      {children}
    </CurrentAddresseeProvider>
  );
};
